/**
 * The builder's clipboard (R4): copy a selection of fields, paste it back.
 *
 * Pure functions over the builder's working copy, so the semantics have a
 * test of their own and the component only decides WHEN. What the design
 * promises and this keeps:
 *
 * - a paste lands after the selected field's row, or at the end of the
 *   container when nothing is selected;
 * - every pasted field gets a FRESH key from the same generator Duplicate
 *   uses (`freeFieldKey`: `email` → `email_2`), and a label that already
 *   exists on the form gets " (copy)";
 * - copied rows keep their layout under fresh row ids, and the 4-per-row cap
 *   still applies (a longer run is split);
 * - references between copied fields (a state list's country, a payment's
 *   amount) point at the copies, as Duplicate's do;
 * - a field pasted into a repeating scope is refused where its type cannot
 *   live there — a payment is charged once per submission, never per item.
 *
 * Cards and widget containers are not clipboard items: the selection holds
 * questions and content blocks, which is what the design's bar counts.
 */
import type { FormWidget } from '../../shared/types';
import {
  BField, Cont, assignCont, cloneField, contOf, countKeysOf, freeFieldKey, inCont, isStructural, remapRefs,
} from './builderModel';

/** What Copy stores: deep copies, in document order, with their original row ids. */
export interface ClipBundle { fields: BField[] }

export const MAX_PER_ROW = 4;

/** Types that only make sense once per submission, so never inside a repeating scope. */
const ONCE_ONLY = new Set(['payment']);

/** Copy `keys` off the form, in the order they appear on it. */
export function copyFields(fields: BField[], keys: Iterable<string>): ClipBundle {
  const want = new Set(keys);
  const picked = fields.filter((f) => want.has(f.key) && !isStructural(f));
  return { fields: picked.map((f) => JSON.parse(JSON.stringify(f)) as BField) };
}

/** May a field of this type be pasted into `c`? */
export function canPasteInto(f: Pick<BField, 'type'>, c: Cont): boolean {
  return c.scope === 'order' || !ONCE_ONLY.has(f.type);
}

/**
 * The fields of the row `key` sits in (itself included), in container order —
 * what a paste lands after.
 */
export function rowOf(fields: BField[], key: string, widgetSecIds: Set<string>): BField[] {
  const f = fields.find((x) => x.key === key);
  if (!f) return [];
  const items = inCont(fields, contOf(f, widgetSecIds), widgetSecIds);
  const i = items.indexOf(f);
  if (i < 0 || !f.row) return [f];
  let s = i, e = i;
  while (s > 0 && items[s - 1].row === f.row) s--;
  while (e < items.length - 1 && items[e + 1].row === f.row) e++;
  return items.slice(s, e + 1);
}

export interface PasteResult {
  fields: BField[];
  /** the keys the copies were given, in order */
  keys: string[];
  /** how many were refused because their type cannot live in the target */
  refused: number;
}

/**
 * Paste `clip` into `target`, after the row holding `afterKey` (null: at the
 * end of the container). See the file comment for what is preserved.
 */
export function pasteFields(o: {
  fields: BField[];
  clip: ClipBundle;
  widgets: FormWidget[];
  target: Cont;
  afterKey: string | null;
  widgetSecIds: Set<string>;
  /** mints a row id nothing on the form uses */
  nextRowId: () => string;
}): PasteResult {
  const { fields, clip, widgets, target, afterKey, widgetSecIds } = o;
  const taken = new Set([...fields.map((f) => f.key), ...countKeysOf(widgets)]);
  const labels = new Set(fields.filter((f) => !isStructural(f)).map((f) => (f.label || '').trim().toLowerCase()).filter(Boolean));
  const keys = new Map<string, string>();
  const copies: BField[] = [];
  let refused = 0;

  // Rows: consecutive copied fields sharing a row id stay together, under a
  // fresh id, at most MAX_PER_ROW to a row.
  const rowIds = new Map<string, string>();
  const rowCount = new Map<string, number>();
  const freshRow = (old: string): string => {
    let id = rowIds.get(old);
    const n = rowCount.get(id || '') || 0;
    if (!id || n >= MAX_PER_ROW) { id = o.nextRowId(); rowIds.set(old, id); rowCount.set(id, 0); }
    rowCount.set(id, (rowCount.get(id) || 0) + 1);
    return id;
  };

  for (const src of clip.fields) {
    if (isStructural(src)) continue;
    if (!canPasteInto(src, target)) { refused += 1; continue; }
    const key = freeFieldKey(src.key, taken);
    taken.add(key);
    keys.set(src.key, key);
    const c = cloneField(src, key);
    const label = (c.label || '').trim();
    if (label && labels.has(label.toLowerCase())) c.label = `${label} (copy)`;
    labels.add((c.label || '').trim().toLowerCase());
    c.row = src.row ? freshRow(src.row) : null;
    assignCont(c, target);
    copies.push(c);
  }
  // A row left with one field in it is not a row.
  for (const c of copies) if (c.row && (rowCount.get(c.row) || 0) < 2) c.row = null;
  for (const c of copies) remapRefs(c, keys);

  if (!copies.length) return { fields, keys: [], refused };
  const out = fields.slice();
  const anchorRow = afterKey ? rowOf(out, afterKey, widgetSecIds) : [];
  let at: number;
  if (anchorRow.length) {
    at = out.indexOf(anchorRow[anchorRow.length - 1]) + 1;
  } else {
    const items = inCont(out, target, widgetSecIds);
    at = items.length ? out.indexOf(items[items.length - 1]) + 1 : out.length;
  }
  out.splice(at, 0, ...copies);
  return { fields: out, keys: copies.map((c) => c.key), refused };
}

/**
 * Move a whole selection to one spot, in document order — what dragging one
 * field of a multi-selection does. Each moved field leaves its row: the drop
 * decides one position, and a run of fields cannot all share it.
 */
export function moveFieldsTo(o: {
  fields: BField[];
  keys: Iterable<string>;
  target: Cont;
  /** land before/after this field's row, or at the end (null) */
  anchorKey: string | null;
  pos: 'above' | 'below' | 'end';
  widgetSecIds: Set<string>;
}): BField[] | null {
  const want = new Set(o.keys);
  const moving = o.fields.filter((f) => want.has(f.key) && !isStructural(f));
  if (!moving.length) return null;
  const movingKeys = new Set(moving.map((f) => f.key));
  if (o.anchorKey && movingKeys.has(o.anchorKey)) return null;
  for (const m of moving) if (!canPasteInto(m, o.target)) return null;
  const rest = o.fields.filter((f) => !movingKeys.has(f.key));
  const moved = moving.map((f) => { const c = { ...f, row: null }; assignCont(c, o.target); return c; });
  let at: number;
  const anchorRow = o.anchorKey ? rowOf(rest, o.anchorKey, o.widgetSecIds) : [];
  if (anchorRow.length && o.pos !== 'end') {
    at = o.pos === 'above' ? rest.indexOf(anchorRow[0]) : rest.indexOf(anchorRow[anchorRow.length - 1]) + 1;
  } else {
    const items = inCont(rest, o.target, o.widgetSecIds);
    at = items.length ? rest.indexOf(items[items.length - 1]) + 1 : rest.length;
  }
  rest.splice(at, 0, ...moved);
  return rest;
}
